/* eslint-disable import/prefer-default-export */
import geocode from 'lib/location/geocode';
import GoogleGeocode from 'lib/location/Locations/Google';
import MaxMindGeolocation from 'lib/location/Locations/MaxMind';
import Geocode from 'types/Geocode';
import SecretsManager from 'aws-sdk/clients/secretsmanager';
import { LocationRecord } from '@maxmind/geoip2-node';

enum Methods {
  Maxmind = 'maxmind',
  Html5 = 'html5',
  Google = 'google',
}

export interface LocationData {
  city: string;
  latitude: number;
  longitude: number;
  stateAbbreviation: string;
  zip: string;
}

export type LatLon =
  | {
      readonly latitude: number;
      readonly longitude: number;
    }
  | {
      readonly latitude: null;
      readonly longitude: null;
    };

export interface LocationSuccessInterface {
  (geocode: Geocode): void;
}

export async function serverSideMaxMindGeolocate(ip: string): Promise<LocationRecord | undefined> {
  // Using a require statement here so that it won't get bundled with webpack,
  // since it relies on `fs` package
  const { WebServiceClient } = await import('@maxmind/geoip2-node');
  const secretsManagerClient = new SecretsManager();
  const { SecretString } = await secretsManagerClient
    .getSecretValue({ SecretId: 'production/autolist-web/maxmind' })
    .promise();

  if (!SecretString) {
    throw Error();
  }

  const { accountId, licenseKey } = JSON.parse(SecretString);

  const client = new WebServiceClient(accountId, licenseKey);

  const result = await client.city(ip);

  return result?.location;
}

function getPosition(options?: Record<string, any>): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    // https://developer.mozilla.org/en-US/docs/Web/API/Geolocation
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
}

async function getCoordsFromBrowser(): Promise<GeolocationCoordinates> {
  const position = await getPosition();

  return position.coords;
}

async function reverseGeocode(coordinates: {
  latitude: number;
  longitude: number;
}): Promise<google.maps.GeocoderResult[]> {
  const location = {
    lat: coordinates.latitude,
    lng: coordinates.longitude,
  };
  return geocode({ location });
}

function cacheInLegacyStorage(method: Methods, location: Geocode): void {
  const userID = localStorage.getItem('User');
  const localStorageKey = `User-${userID}`;

  if (!userID) {
    // No key to cache on
    return;
  }

  const rawUserData = localStorage.getItem(localStorageKey);

  const userData = rawUserData ? JSON.parse(rawUserData) : {};

  const newUserData = Object.assign({}, userData, {
    [`${method}_data`]: location,
  });

  localStorage.setItem(localStorageKey, JSON.stringify(newUserData));
}

async function withCache<T>(key: string, compute: () => Promise<T>): Promise<T> {
  const cachedVal = localStorage.getItem(key);
  if (cachedVal) {
    return JSON.parse(cachedVal) as T;
  }
  const computedVal = await compute();
  localStorage.setItem(key, JSON.stringify(computedVal));
  return computedVal;
}

async function maxMindGeolocate(): Promise<Geocode> {
  // http://dev.maxmind.com/geoip/geoip2/javascript/

  const res = await withCache('maxmind_location', () => new Promise(geoip2.city));
  const location = new MaxMindGeolocation(res);

  cacheInLegacyStorage(Methods.Maxmind, location);

  return location;
}

async function html5Geolocate(): Promise<Geocode> {
  const googleData = await withCache('html5_location', async () => {
    const coordinates = await getCoordsFromBrowser();
    return reverseGeocode(coordinates);
  });
  const location = new GoogleGeocode(googleData[0]);

  cacheInLegacyStorage(Methods.Html5, location);

  return location;
}

interface FailureHandler {
  (args: any): void;
}

// To get the best of both worlds (accuracy via HTML5 and speed via Maxmind)
// use this method to geolocate by both at the same time. However, this means
// that your callbacks may be invoked up to 2 times each, so only use this if
// handling the response is inexpensive (i.e. NOT running a search)
export async function parallelGeolocate(
  success: LocationSuccessInterface,
  failure: FailureHandler = (): void => {}
): Promise<void> {
  // There's probably a fancier way to do the task/promise cancellation
  let isAwaitingHtml5 = true;

  html5Geolocate()
    .then((...args) => {
      isAwaitingHtml5 = false;
      success(...args);
    })
    .catch(failure);
  maxMindGeolocate()
    .then((...args) => {
      if (isAwaitingHtml5) {
        success(...args);
      }
    })
    .catch((...args) => {
      if (isAwaitingHtml5) {
        failure(...args);
      }
    });
}

export async function clientSideGeolocate(): Promise<Geocode> {
  try {
    return maxMindGeolocate();
  } catch (e) {
    return html5Geolocate();
  }
}
